import _find from 'lodash/find'

import defaultImage from '../assets/images/default-accountant.png'

import config from '../config'
import {
    BUSINESS_VEHICLE_TYPE_BICYCLE,
    BUSINESS_VEHICLE_TYPE_CAR,
    BUSINESS_VEHICLE_TYPE_MOTORBIKE,
    BUSINESS_VEHICLE_TYPE_VAN,
    FILING_AREA_GENERAL,
    SECTION_STATUS_ORDER,
    SELF_ASSESSMENT_STATUS_ORDER,
} from '../constants'

export { copyTextToClipboard } from './clipboard'
export {
    dateDifference,
    formatDate,
    formatDateWithTime,
    formatTaxYear,
    formatTaxYearDateRange,
    isCloseToDeadline,
    isDateOverLimit,
    isDatePast,
    isTaxYearEnded,
    isTodayWeekend,
} from './dates'
export * from './get-current-or-mocked-date'
export { removeItemsById, shouldGoToNextAllowanceQuestion, shouldGoToNextQuestion, updateOrAddItemsById } from './redux'
export { capitalizeFirstLetter, formatCurrency, fromString, hexToRgbA } from './strings'
export { isValidCompaniesHouseAuthCode, isValidNino, isValidUtr } from './validation'

export const accountantImageUrl = (accountantId) => {
    if (!accountantId) return defaultImage
    return `${config.apiUrl}accountant/${accountantId}/image`
}

export const isSelfAssessmentStatusAtLeast = (currentStatus, neededStatus) =>
    SELF_ASSESSMENT_STATUS_ORDER[currentStatus] >= SELF_ASSESSMENT_STATUS_ORDER[neededStatus]

export const isSectionStatusAtLeast = (currentStatus, neededStatus) =>
    SECTION_STATUS_ORDER[currentStatus] >= SECTION_STATUS_ORDER[neededStatus]

export const hasGeneralAllowancesUploads = (allowances) =>
    _find(allowances, {
        section: FILING_AREA_GENERAL,
        isActive: true,
        isHidden: false,
        hasUploads: true,
    })

export const hasFilledProfile = ({ profile }) =>
    !!(
        profile.addressCity &&
        profile.addressPostcode &&
        profile.addressStreetAddress &&
        profile.dateOfBirth &&
        profile.firstName &&
        profile.lastName &&
        profile.maritalStatus &&
        (profile.hasNoNationalInsuranceNumber || profile.nationalInsuranceNumber) &&
        profile.phoneNumber
    )

export const hasFilledUTR = ({ profile }) => !!profile.uniqueTaxpayerReference

export const hasFilledDateOfBirth = ({ profile }) => !!profile.dateOfBirth

export const getDeductionsFromMiles = (miles, vehicleType = BUSINESS_VEHICLE_TYPE_CAR) => {
    let lowerRate = 0.45
    let higherRate = 0.25

    switch (vehicleType) {
        case BUSINESS_VEHICLE_TYPE_MOTORBIKE:
            lowerRate = 0.24
            higherRate = 0.24
            break
        case BUSINESS_VEHICLE_TYPE_BICYCLE:
            lowerRate = 0.2
            higherRate = 0.2
            break
        case BUSINESS_VEHICLE_TYPE_CAR:
        case BUSINESS_VEHICLE_TYPE_VAN:
        default:
            break
    }

    const higherRateMiles = miles > 10000 ? 10000 : miles
    const lowerRateMiles = miles > 10000 ? miles - 10000 : 0
    return Math.round(higherRateMiles * higherRate + lowerRateMiles * lowerRate)
}

export const personalAllowance = ({ taxYear }) => {
    switch (taxYear) {
        case 2013:
            return 8105
        case 2014:
            return 9440
        case 2015:
            return 10000
        case 2016:
            return 10600
        case 2017:
            return 11000
        case 2018:
            return 11500
        case 2019:
            return 11850
        case 2020:
            return 12500
        default:
            return 12500
    }
}

export const handleBooleanStringValue = (v) => {
    if (v === 'true') return true
    if (v === 'false') return false
    return v
}

export const freezeBody = () => {
    document.getElementsByTagName('body')[0].style.overflow = 'hidden'
}

export const unfreezeBody = () => {
    document.getElementsByTagName('body')[0].style.overflow = 'auto'
}

export const formatUkPhoneNumber = (phoneNumber) => {
    const trimmedPhoneNumber = phoneNumber.replace(/\s/g, '')

    if (trimmedPhoneNumber.startsWith('0')) {
        return trimmedPhoneNumber.replace(trimmedPhoneNumber.charAt(0), '44')
    }

    if (trimmedPhoneNumber.startsWith('+0')) {
        return trimmedPhoneNumber.replace('+0', '44')
    }

    return trimmedPhoneNumber
}

export const getDefaultPdfSettings = (
    {
        margin = 1.5,
        filename = 'File.pdf',
        image: { type, quality } = { type: 'jpeg', quality: 0.98 },
        html2canvas: { dpi, letterRendering, scrollY } = { dpi: 192, letterRendering: true, scrollY: 0 },
        jsPDF: { unit, format, orientation } = { unit: 'cm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode } = { mode: ['avoid-all'] },
    } = {
        margin: 1.5,
        filename: 'File.pdf',
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { dpi: 192, letterRendering: true, scrollY: 0 },
        jsPDF: { unit: 'cm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['avoid-all'] },
    }
) => ({
    margin: margin ?? 1.5,
    filename: filename ?? 'File.pdf',
    image: { type: type ?? 'jpeg', quality: quality ?? 0.98 },
    html2canvas: { dpi: dpi ?? 192, letterRendering: letterRendering ?? true, scrollY: scrollY ?? 0 },
    jsPDF: { unit: unit ?? 'cm', format: format ?? 'a4', orientation: orientation ?? 'portrait' },
    pagebreak: { mode: mode ?? ['avoid-all'] },
})
